import React from "react";
import siteImage from "../images/newheroimage.png";
import { motion } from "framer-motion";
import { Link } from "react-scroll";
import { CheckCircle, ArrowRight, Mail } from "lucide-react";

const MissionVision = () => {
  return (
    <section
      id="home"
      className="relative min-h-[60vh] lg:min-h-[calc(100vh-5rem)] w-full overflow-hidden scroll-mt-20"
      aria-label="Trust Import and Export hero"
    >
      {/* Background with pronounced torn edges */}
      <svg
        className="absolute inset-0 h-full w-full pointer-events-none select-none hidden lg:block"
        viewBox="0 0 1600 900"
        preserveAspectRatio="xMidYMid slice"
        role="img"
        aria-label="Agricultural products with torn paper edge"
      >
        <defs>
          <style>{`
            .mv-desktop { display: block; }
            .mv-mobile { display: none; }
            @media (max-width: 640px) {
              .mv-desktop { display: none; }
              .mv-mobile { display: block; }
            }
          `}</style>

          <filter
            id="edgeShadowHV"
            x="-25%"
            y="-25%"
            width="150%"
            height="150%"
            colorInterpolationFilters="sRGB"
          >
            <feDropShadow
              dx="0"
              dy="10"
              stdDeviation="12"
              floodColor="#000000"
              floodOpacity="0.28"
            />
          </filter>

          <filter
            id="innerShadeHV"
            x="-10%"
            y="-10%"
            width="120%"
            height="120%"
          >
            <feGaussianBlur in="SourceAlpha" stdDeviation="2" result="blur" />
            <feOffset dy="1" result="offset" />
            <feComposite
              in="offset"
              in2="SourceAlpha"
              operator="out"
              result="edgeOnly"
            />
            <feColorMatrix
              in="edgeOnly"
              type="matrix"
              values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.26 0"
            />
          </filter>

          <linearGradient id="overlayHV" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#000000" stopOpacity="0.65" />
            <stop offset="40%" stopColor="#000000" stopOpacity="0.45" />
            <stop offset="70%" stopColor="#000000" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#000000" stopOpacity="0.0" />
          </linearGradient>

          <filter
            id="deckleJitterDesktop"
            x="-10%"
            y="-10%"
            width="120%"
            height="120%"
          >
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.015"
              numOctaves="4"
              seed="7"
              result="noise"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="noise"
              scale="80"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>

          <filter
            id="deckleJitterMobile"
            x="-10%"
            y="-10%"
            width="120%"
            height="120%"
          >
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.018"
              numOctaves="2"
              seed="11"
              result="noise"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="noise"
              scale="30"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>

          <mask
            id="deckleMaskHV"
            x="0"
            y="0"
            width="1600"
            height="900"
            maskUnits="userSpaceOnUse"
          >
            <rect x="0" y="0" width="1600" height="900" fill="#ffffff" />

            <g className="mv-desktop" filter="url(#deckleJitterDesktop)" transform="translate(0, 48)">
              <path
                fill="#000000"
                d="
                  M0,600
                  C60,680 120,640 180,720
                  C240,620 300,710 360,630
                  C420,720 480,640 540,730
                  C600,620 660,715 720,635
                  C780,725 840,650 900,720
                  C960,625 1020,710 1080,645
                  C1140,735 1200,660 1260,730
                  C1320,630 1380,715 1440,650
                  C1500,740 1550,690 1600,715
                  L1600,900 L0,900 Z
                "
              />
            </g>

            <g className="mv-mobile" filter="url(#deckleJitterMobile)" transform="translate(0, 72)">
              <path
                fill="#000000"
                d="
                  M0,780
                  C80,835 160,800 240,850
                  C320,790 400,840 480,805
                  C560,855 640,815 720,850
                  C800,800 880,840 960,810
                  C1040,855 1120,815 1200,845
                  C1280,810 1360,850 1440,815
                  C1520,860 1560,835 1600,845
                  L1600,900 L0,900 Z
                "
              />
            </g>
          </mask>
        </defs>

        <g mask="url(#deckleMaskHV)" filter="url(#edgeShadowHV)">
          <image
            href={siteImage}
            x="0"
            y="0"
            width="1600"
            height="900"
            preserveAspectRatio="xMidYMid slice"
          />
          {/* Mobile: uniform dark overlay to improve readability on phones (SVG fallback) */}
          <rect
            className="mv-mobile"
            x="0"
            y="0"
            width="1600"
            height="900"
            fill="#000000"
            opacity="0.48"
            filter="url(#innerShadeHV)"
          />
        </g>
      </svg>

      {/* Mobile-only image card: placed before overlay so overlay can darken it */}
      <div className="lg:hidden mt-6 w-full flex justify-center z-0">
        <div className="w-11/12 rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/10">
          <img
            src={siteImage}
            alt="hero"
            className="w-full h-64 sm:h-72 object-cover"
          />
        </div>
      </div>

      {/* DOM overlay only on mobile for reliable darkening (sits above image, below text) */}
      <div className="absolute inset-0 pointer-events-none lg:hidden z-10">
        <div className="absolute inset-0 bg-black/48" />
      </div>

      {/* Content */}
  <div className="relative z-20 container h-full flex items-start md:items-center pt-16 sm:pt-20 md:pt-24 pb-8 md:pb-14 px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center w-full">
          {/* Left Content */}
          <motion.div
            className="text-black text-center lg:text-left"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.div
              className="space-y-3 sm:space-y-4 text-black"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <motion.h1
                className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight drop-shadow-2xl"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                Exporting Ethiopia's
                <motion.span
                  className="block text-transparent bg-clip-text bg-gradient-to-r from-brand-olive to-brand-yellow drop-shadow-2xl"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                >
                  Agricultural Excellence
                </motion.span>
              </motion.h1>

              <motion.p
                className="text-base sm:text-lg md:text-xl text-gray-800 leading-relaxed drop-shadow-lg max-w-2xl mx-auto lg:mx-0"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.0 }}
              >
                Leading exporter of premium beans, pulses,<br /> oil seeds, and spices
                to USA, Europe, Asia, Africa, <br />and North America. <br />Quality is our
                commitment.
              </motion.p>
            </motion.div>

            {/* Enhanced CTAs */}
            <motion.div
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start mt-6 sm:mt-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
            >
              <Link to="services" smooth duration={500} offset={-80}>
                <motion.div
                  className="group inline-flex items-center justify-center gap-3 rounded-full bg-brand-yellow text-brand-navy font-semibold px-6 py-3 sm:px-8 sm:py-4 shadow-lg shadow-brand-yellow/30 hover:shadow-brand-yellow/50 hover:bg-brand-gold transition-all duration-300 text-sm sm:text-base cursor-pointer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Explore Our Products
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
                </motion.div>
              </Link>
              <Link to="contact" smooth duration={500} offset={-80}></Link>
            </motion.div>
          </motion.div>
        </div>

        
      </div>

      <div className="pointer-events-none absolute -bottom-16 -right-16 h-64 w-64 rounded-full bg-brand-yellow/20 blur-3xl animate-pulse" />
      <div className="pointer-events-none absolute -top-16 -left-16 h-64 w-64 rounded-full bg-brand-yellow/10 blur-3xl animate-pulse" />
    </section>
  );
};

export default MissionVision;
